// ============================================================================
// CLEO'S SECRET — WeeklyModule
// File: src/pages/WeeklyModule.jsx
//
// Single-week deep view rendered when a patient clicks an unlocked week
// block on TwelveWeekTimeline (ProgramDashboard's Zone 2).
//
// Five sections (top to bottom):
//   1. Lead video — iframe embed if leadVideoUrl set; SAND placeholder
//      otherwise. Auto-fires 'video_watched' mark-progress after 30s on page.
//   2. The science — MarkdownRender + "I've read this" button →
//      'science_read' mark-progress.
//   3. The protocol — same pattern → 'protocol_acknowledged'.
//   4. Resources — chip-list of {type, name, url} (hidden if empty/null).
//   5. Check-in — CheckinSliders (4 Likert) + free-text + submit. Once
//      submitted, renders as a read-only summary with "Update my check-in"
//      affordance.
//
// Module completion fires inline server-side when all 3 progress timestamps
// + a checkin row exist; the response carries moduleCompleted: true and we
// update local state to show the "Module complete" banner without refetch.
//
// Render branches: loading / no-email / invalid-week / fetch-error /
// not-accessible (locked) / main (accessible).
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  P, T, FF, FS,
  Eyebrow, BtnPrimary, BtnGhost, Spinner, ErrorState,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';
import MarkdownRender from '../components/program/MarkdownRender';
import CheckinSliders from '../components/program/CheckinSliders';

// ─── Helpers ────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

var PHASE_LABELS = {
  foundations: 'Foundations',
  recalibration: 'Recalibration',
  optimisation: 'Optimisation',
};

function phasePill(phase) {
  if (phase === 'foundations')   return { bg: 'rgba(201, 169, 97, 0.10)', fg: P.BR2, border: 'rgba(201, 169, 97, 0.35)' };
  if (phase === 'recalibration') return { bg: 'rgba(154, 116, 48, 0.12)', fg: P.BR2, border: 'rgba(154, 116, 48, 0.40)' };
  if (phase === 'optimisation')  return { bg: 'rgba(90, 63, 24, 0.10)',   fg: P.BR2, border: 'rgba(90, 63, 24, 0.40)' };
  return { bg: P.SAND, fg: P.TF, border: P.SAND3 };
}

// Section eyebrow used for The science / The protocol / Resources / Check-in
function SectionEyebrow({ text }) {
  return (
    <div style={{
      fontFamily: FF,
      fontSize: 'clamp(11px, 0.95vw, 13px)',
      fontWeight: 700,
      letterSpacing: '0.32em',
      textTransform: 'uppercase',
      color: P.BR2,
      marginBottom: 'clamp(14px, 1.6vw, 18px)',
      paddingBottom: 'clamp(8px, 1vw, 12px)',
      borderBottom: '1px solid rgba(154,116,48,0.22)',
    }}>{text}</div>
  );
}

function ReadCheckmark() {
  return (
    <svg width={14} height={14} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8.5 L7 12 L13 4" stroke={P.GREEN} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── WeekHeader — SAND strip mirroring ProgramDashboard's ProgramHeader ───
function WeekHeader({ module, onBack }) {
  var pill = phasePill(module.phase);
  return (
    <header style={{
      background: P.SAND,
      padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 3vw, 40px) clamp(28px, 3.5vw, 48px)',
      textAlign: 'center',
      borderBottom: '1px solid ' + P.SAND3,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      {/* Back link top-left */}
      <div style={{
        alignSelf: 'flex-start',
        marginBottom: 'clamp(20px, 2.4vw, 28px)',
      }}>
        <button
          onClick={onBack}
          style={{
            fontFamily: FS,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 600,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            padding: '8px 14px',
            background: 'transparent',
            color: P.BR2,
            border: '1px solid ' + P.SAND3,
            cursor: 'pointer',
          }}>← Back to program</button>
      </div>

      <div style={{ marginBottom: 'clamp(16px, 2vw, 24px)', display: 'flex', justifyContent: 'center' }}>
        <Ankh responsive="opener" color={P.GO} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <Eyebrow center>Week {String(module.weekNumber).padStart(2, '0')} of 12</Eyebrow>
      </div>
      <h1 style={{
        fontFamily: FF,
        fontSize: 'clamp(32px, 4.4vw, 60px)',
        fontWeight: 400,
        lineHeight: 1.08,
        letterSpacing: '-0.018em',
        color: P.INK,
        margin: 0,
        maxWidth: 760,
      }}>{module.title}</h1>
      {module.subtitle && (
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(18px, 1.8vw, 24px)',
          fontStyle: 'italic',
          color: P.BR2,
          margin: 'clamp(14px, 1.6vw, 20px) 0 0',
          maxWidth: 640,
          lineHeight: 1.4,
        }}>{module.subtitle}</p>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center',
        marginTop: 'clamp(18px, 2vw, 24px)',
      }}>
        <span style={{
          display: 'inline-block',
          fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 11px)',
          fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase',
          padding: '5px 10px',
          background: pill.bg, color: pill.fg, border: '1px solid ' + pill.border,
        }}>{PHASE_LABELS[module.phase] || module.phase}</span>
        {module.isMandatoryMilestone && (
          <span style={{
            display: 'inline-block',
            fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 11px)',
            fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase',
            padding: '5px 10px',
            background: P.GO, color: P.INK, border: '1px solid ' + P.GO,
          }}>{module.milestoneLabel || 'Milestone'}</span>
        )}
      </div>
    </header>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// WeeklyModule
// ═══════════════════════════════════════════════════════════════════════
export default function WeeklyModule(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;
  var routePayload = props.routePayload || {};
  var rawWeek = routePayload.week;
  var weekNumber = parseInt(rawWeek, 10);
  var weekValid = !isNaN(weekNumber) && weekNumber >= 1 && weekNumber <= 12;

  // Fetched data + loading/error
  var dataState = useState(null);
  var data = dataState[0]; var setData = dataState[1];
  var loadingState = useState(true);
  var loading = loadingState[0]; var setLoading = loadingState[1];
  var errorState = useState(null);
  var error = errorState[0]; var setError = errorState[1];

  // Auth-wait
  var emailWaitState = useState(0);
  var emailWaitMs = emailWaitState[0]; var setEmailWaitMs = emailWaitState[1];

  // Local progress + checkin state (updated by mutations without refetch)
  var progressState = useState(null);
  var progress = progressState[0]; var setProgress = progressState[1];
  var checkinState = useState(null);
  var checkin = checkinState[0]; var setCheckin = checkinState[1];

  // Video 30s timer flag
  var video30sFiredState = useState(false);
  var video30sFired = video30sFiredState[0]; var setVideo30sFired = video30sFiredState[1];

  // Mark-progress in-flight flags
  var markingState = useState({ video: false, science: false, protocol: false });
  var marking = markingState[0]; var setMarking = markingState[1];

  // Check-in form state
  var scoresState = useState({ hormonalScore: 5, metabolicScore: 5, sleepScore: 5, vitalityScore: 5 });
  var scores = scoresState[0]; var setScores = scoresState[1];
  var freeTextState = useState('');
  var freeText = freeTextState[0]; var setFreeText = freeTextState[1];
  var submittingState = useState(false);
  var submitting = submittingState[0]; var setSubmitting = submittingState[1];
  var submitErrorState = useState(null);
  var submitError = submitErrorState[0]; var setSubmitError = submitErrorState[1];
  var submitSuccessState = useState(false);
  var submitSuccess = submitSuccessState[0]; var setSubmitSuccess = submitSuccessState[1];
  var resubmitOpenState = useState(false);
  var resubmitOpen = resubmitOpenState[0]; var setResubmitOpen = resubmitOpenState[1];

  // ── Auth-wait loop ─────────────────────────────────────────────────
  useEffect(function() {
    if (email) return;
    if (emailWaitMs >= 3000) { setLoading(false); return; }
    var t = setTimeout(function() { setEmailWaitMs(emailWaitMs + 500); }, 500);
    return function() { clearTimeout(t); };
  }, [email, emailWaitMs]);

  // ── Fetch on mount + email change ──────────────────────────────────
  useEffect(function() {
    if (!email) return;
    if (!weekValid) { setLoading(false); return; }
    var cancelled = false;
    setLoading(true);
    setError(null);

    fetch('/api/get-week-module', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, weekNumber: weekNumber }),
    })
      .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
      .then(function(resp) {
        if (cancelled) return;
        if (resp.ok) {
          setData(resp.body);
          if (resp.body.progress) setProgress(resp.body.progress);
          if (resp.body.checkin) {
            setCheckin(resp.body.checkin);
            setScores({
              hormonalScore:  resp.body.checkin.hormonalScore  || 5,
              metabolicScore: resp.body.checkin.metabolicScore || 5,
              sleepScore:     resp.body.checkin.sleepScore     || 5,
              vitalityScore:  resp.body.checkin.vitalityScore  || 5,
            });
            if (resp.body.checkin.freeText) setFreeText(resp.body.checkin.freeText);
          }
          setLoading(false);
        } else {
          setError((resp.body && resp.body.error) || 'Could not load this week');
          setLoading(false);
        }
      })
      .catch(function() {
        if (cancelled) return;
        setError('Network error');
        setLoading(false);
      });

    return function() { cancelled = true; };
  }, [email, weekNumber, weekValid]);

  // ── Video 30s timer ────────────────────────────────────────────────
  useEffect(function() {
    if (!data || !data.module || !data.module.leadVideoUrl) return;
    if (progress && progress.videoWatchedAt) return;
    if (video30sFired) return;

    var t = setTimeout(function() {
      setVideo30sFired(true);
      fireProgressEvent('video_watched');
    }, 30000);
    return function() { clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, progress, video30sFired]);

  // ── Mark-progress helper ───────────────────────────────────────────
  function fireProgressEvent(event) {
    // Map event to local marking key
    var markKey = event === 'video_watched' ? 'video'
                : event === 'science_read' ? 'science'
                : event === 'protocol_acknowledged' ? 'protocol'
                : null;
    if (!markKey) return;
    if (marking[markKey]) return; // already in flight

    setMarking(function(prev) {
      var next = Object.assign({}, prev); next[markKey] = true; return next;
    });

    fetch('/api/mark-module-progress', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, weekNumber: weekNumber, event: event }),
    })
      .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
      .then(function(resp) {
        setMarking(function(prev) {
          var next = Object.assign({}, prev); next[markKey] = false; return next;
        });
        if (resp.ok && resp.body.success) {
          setProgress(resp.body.progress);
        }
      })
      .catch(function() {
        setMarking(function(prev) {
          var next = Object.assign({}, prev); next[markKey] = false; return next;
        });
      });
  }

  // ── Submit check-in ────────────────────────────────────────────────
  function handleSubmitCheckin() {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    var trimmedFreeText = freeText.trim();
    fetch('/api/submit-checkin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        weekNumber: weekNumber,
        hormonalScore:  scores.hormonalScore,
        metabolicScore: scores.metabolicScore,
        sleepScore:     scores.sleepScore,
        vitalityScore:  scores.vitalityScore,
        freeText: trimmedFreeText === '' ? null : trimmedFreeText,
      }),
    })
      .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
      .then(function(resp) {
        setSubmitting(false);
        if (resp.ok && resp.body.success) {
          setCheckin(resp.body.checkin);
          if (resp.body.moduleCompleted) {
            setProgress(function(prev) {
              return Object.assign({}, prev || {}, { moduleCompletedAt: new Date().toISOString() });
            });
          }
          setSubmitSuccess(true);
          setResubmitOpen(false);
          setTimeout(function() { setSubmitSuccess(false); }, 2500);
        } else {
          setSubmitError((resp.body && resp.body.error) || 'Could not submit check-in');
        }
      })
      .catch(function() {
        setSubmitting(false);
        setSubmitError('Network error');
      });
  }

  // ═════════════════════════════════════════════════════════════════════
  // Render branches
  // ═════════════════════════════════════════════════════════════════════

  // Loading
  if (loading) {
    return (
      <div style={{
        fontFamily: FF, background: P.SAND, minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Spinner color={P.GO} size={48} />
      </div>
    );
  }

  // Invalid week parameter (no routePayload.week or out of 1-12)
  if (!weekValid) {
    return (
      <ErrorState
        premise="Something is missing."
        title="Week number is not specified."
        action={
          <BtnPrimary onClick={function() { if (onNavigate) onNavigate('program'); }}>
            Return to program
          </BtnPrimary>
        }
      />
    );
  }

  // Not signed in
  if (!email) {
    return (
      <ErrorState
        premise="Sign in to see this week."
        title="Your portal awaits."
        action={
          <BtnPrimary onClick={function() { if (onNavigate) onNavigate('login', { returnTo: 'program' }); }}>
            Sign in
          </BtnPrimary>
        }
      />
    );
  }

  // Fetch error
  if (error || !data) {
    return (
      <ErrorState
        premise="Something is missing."
        title={error || 'Could not load this week.'}
        action={
          <BtnPrimary onClick={function() { if (onNavigate) onNavigate('program'); }}>
            Return to program
          </BtnPrimary>
        }
      />
    );
  }

  // Locked (server says accessible=false)
  if (!data.accessible) {
    var cw = data.currentWeek != null ? data.currentWeek : 1;
    return (
      <div style={{ fontFamily: FF, background: P.SAND, minHeight: '100vh' }}>
        <main style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: 'clamp(60px, 8vw, 120px) clamp(24px, 4vw, 64px)',
          textAlign: 'center',
        }}>
          <div style={{ marginBottom: 'clamp(24px, 3vw, 36px)', display: 'flex', justifyContent: 'center' }}>
            <Ankh responsive="opener" color={P.GO} />
          </div>
          <p style={{
            ...T.premise, color: P.BR2,
            marginBottom: 'clamp(20px, 2.4vw, 28px)',
          }}>This week is not yet unlocked.</p>
          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(28px, 3.4vw, 44px)',
            fontWeight: 400, lineHeight: 1.12,
            color: P.INK, margin: '0 0 clamp(20px, 2.4vw, 28px)',
          }}>Week {String(weekNumber).padStart(2, '0')}.</h1>
          <div style={{
            width: 'clamp(140px, 18vw, 220px)', height: 2,
            background: P.GO, opacity: 0.9,
            margin: '0 auto clamp(28px, 3vw, 40px)',
          }} />
          <p style={{
            ...T.bodyLg, color: P.TX, lineHeight: 1.85,
            maxWidth: 520, margin: '0 auto clamp(32px, 3.6vw, 48px)',
          }}>
            You are currently on Week {String(cw).padStart(2, '0')}. Finish that week&rsquo;s check-in and wait seven days for the next week to open.
          </p>
          <BtnPrimary onClick={function() { if (onNavigate) onNavigate('program'); }} large>
            Return to program
          </BtnPrimary>
        </main>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════
  // Main render — accessible week
  // ═════════════════════════════════════════════════════════════════════
  var mod = data.module;
  var moduleCompleted = !!(progress && progress.moduleCompletedAt);
  var videoWatched = !!(progress && progress.videoWatchedAt);
  var scienceRead = !!(progress && progress.scienceReadAt);
  var protocolAcknowledged = !!(progress && progress.protocolAcknowledgedAt);

  var resources = Array.isArray(mod.resources) ? mod.resources : null;
  var hasResources = resources && resources.length > 0;

  var hasCheckin = !!checkin;
  var showFormView = !hasCheckin || resubmitOpen;

  return (
    <div style={{ fontFamily: FF, background: P.SAND, minHeight: '100vh' }}>
      <WeekHeader module={mod} onBack={function() { if (onNavigate) onNavigate('program'); }} />

      <main style={{
        maxWidth: 880,
        margin: '0 auto',
        padding: 'clamp(32px, 4vw, 56px) clamp(20px, 3.5vw, 48px) clamp(64px, 8vw, 120px)',
      }}>

        {/* Module-complete banner */}
        {moduleCompleted && (
          <div style={{
            background: P.GREEN_BG,
            border: '1px solid ' + P.GREEN,
            borderLeft: '4px solid ' + P.GREEN,
            padding: 'clamp(18px, 2vw, 24px) clamp(22px, 2.4vw, 28px)',
            marginBottom: 'clamp(28px, 3.2vw, 40px)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}>
            <ReadCheckmark />
            <div>
              <div style={{
                fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase',
                color: P.GREEN, marginBottom: 4,
              }}>Week complete</div>
              <div style={{
                fontFamily: FF, fontSize: 'clamp(15px, 1.3vw, 17px)',
                color: P.TX, lineHeight: 1.55,
              }}>
                You&rsquo;ve completed Week {String(mod.weekNumber).padStart(2, '0')} on {formatDate(progress.moduleCompletedAt)}.
              </div>
            </div>
          </div>
        )}

        {/* ─── Section 1: Lead video ─── */}
        <section style={{ marginBottom: 'clamp(40px, 4.5vw, 56px)' }}>
          <SectionEyebrow text="Lead video" />
          {mod.leadVideoUrl ? (
            <div style={{
              background: P.OBS,
              border: '1px solid ' + P.GO,
              position: 'relative',
              paddingBottom: '56.25%', // 16:9
              height: 0,
              overflow: 'hidden',
            }}>
              <iframe
                src={mod.leadVideoUrl}
                title={mod.title + ' — lead video'}
                style={{
                  position: 'absolute',
                  top: 0, left: 0,
                  width: '100%', height: '100%',
                  border: 'none',
                }}
                allow="autoplay; fullscreen; picture-in-picture"
                sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
              />
            </div>
          ) : (
            <div style={{
              background: 'rgba(232,221,196,0.55)',
              border: '1px dashed ' + P.SAND3,
              padding: 'clamp(48px, 6vw, 72px) clamp(24px, 3vw, 40px)',
              textAlign: 'center',
            }}>
              <p style={{
                fontFamily: FF, fontSize: 'clamp(15px, 1.3vw, 17px)',
                fontStyle: 'italic', color: P.BR2,
                margin: 0, lineHeight: 1.6,
              }}>
                Lead video in development by Robyn O&rsquo;Sullivan, GP.
              </p>
            </div>
          )}
          {videoWatched && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginTop: 12,
              fontFamily: FF, fontSize: 'clamp(13px, 1.1vw, 15px)',
              fontWeight: 600, color: P.GREEN,
            }}>
              <ReadCheckmark />
              <span>Watched</span>
            </div>
          )}
        </section>

        {/* ─── Section 2: The science ─── */}
        <section style={{ marginBottom: 'clamp(40px, 4.5vw, 56px)' }}>
          <SectionEyebrow text="The science" />
          {mod.theScience ? (
            <MarkdownRender markdown={mod.theScience} />
          ) : (
            <p style={{
              fontFamily: FF, fontSize: 'clamp(16px, 1.4vw, 18px)',
              fontStyle: 'italic', color: P.BR2,
              lineHeight: 1.7, margin: 0,
            }}>
              The science of {mod.title.toLowerCase()} — written by your clinical team. Available before this week unlocks.
            </p>
          )}
          {mod.theScience && (
            <div style={{ marginTop: 'clamp(20px, 2.2vw, 28px)', textAlign: 'right' }}>
              {scienceRead ? (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontFamily: FF, fontSize: 'clamp(13px, 1.1vw, 15px)',
                  fontWeight: 600, color: P.GREEN,
                  padding: '10px 18px',
                  border: '1px solid ' + P.GREEN,
                  background: P.GREEN_BG,
                }}>
                  <ReadCheckmark />
                  <span>Read</span>
                </span>
              ) : (
                <BtnGhost
                  onClick={function() { fireProgressEvent('science_read'); }}
                  disabled={marking.science}
                >{marking.science ? 'Marking…' : 'I’ve read this'}</BtnGhost>
              )}
            </div>
          )}
        </section>

        {/* ─── Section 3: The protocol ─── */}
        <section style={{ marginBottom: 'clamp(40px, 4.5vw, 56px)' }}>
          <SectionEyebrow text="The protocol" />
          {mod.theProtocol ? (
            <MarkdownRender markdown={mod.theProtocol} />
          ) : (
            <p style={{
              fontFamily: FF, fontSize: 'clamp(16px, 1.4vw, 18px)',
              fontStyle: 'italic', color: P.BR2,
              lineHeight: 1.7, margin: 0,
            }}>
              This week&rsquo;s protocol is being prepared. Available before this week unlocks.
            </p>
          )}
          {mod.theProtocol && (
            <div style={{ marginTop: 'clamp(20px, 2.2vw, 28px)', textAlign: 'right' }}>
              {protocolAcknowledged ? (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontFamily: FF, fontSize: 'clamp(13px, 1.1vw, 15px)',
                  fontWeight: 600, color: P.GREEN,
                  padding: '10px 18px',
                  border: '1px solid ' + P.GREEN,
                  background: P.GREEN_BG,
                }}>
                  <ReadCheckmark />
                  <span>Read</span>
                </span>
              ) : (
                <BtnGhost
                  onClick={function() { fireProgressEvent('protocol_acknowledged'); }}
                  disabled={marking.protocol}
                >{marking.protocol ? 'Marking…' : 'I’ve read this'}</BtnGhost>
              )}
            </div>
          )}
        </section>

        {/* ─── Section 4: Resources (conditional) ─── */}
        {hasResources && (
          <section style={{ marginBottom: 'clamp(40px, 4.5vw, 56px)' }}>
            <SectionEyebrow text="Resources" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {resources.map(function(r, i) {
                return (
                  <a
                    key={i}
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: 'clamp(12px, 1.4vw, 16px) clamp(16px, 1.8vw, 22px)',
                      background: P.PAP,
                      border: '1px solid ' + P.SAND3,
                      borderLeft: '3px solid ' + P.GO,
                      textDecoration: 'none',
                      color: P.INK,
                      fontFamily: FF,
                      fontSize: 'clamp(14px, 1.2vw, 16px)',
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={function(e) { e.currentTarget.style.borderColor = P.BR; }}
                    onMouseLeave={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
                  >
                    {r.type && (
                      <span style={{
                        fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 11px)',
                        fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase',
                        color: P.BR2,
                        marginRight: 4,
                      }}>{r.type}</span>
                    )}
                    <span style={{ fontWeight: 500 }}>{r.name}</span>
                  </a>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── Section 5: Check-in ─── */}
        <section style={{ marginBottom: 'clamp(40px, 4.5vw, 56px)' }}>
          <SectionEyebrow text="Weekly check-in" />

          {hasCheckin && !resubmitOpen ? (
            // Read-only summary view
            <div style={{
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              padding: 'clamp(24px, 2.8vw, 32px)',
            }}>
              <div style={{
                fontFamily: FF, fontSize: 'clamp(13px, 1.1vw, 15px)',
                fontStyle: 'italic', color: P.TF,
                marginBottom: 16,
              }}>
                Submitted on {formatDate(checkin.submittedAt)}
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 16,
                marginBottom: 'clamp(20px, 2.4vw, 28px)',
              }}>
                {[
                  { label: 'Hormonal balance', value: checkin.hormonalScore },
                  { label: 'Metabolic health', value: checkin.metabolicScore },
                  { label: 'Sleep and recovery', value: checkin.sleepScore },
                  { label: 'Vitality and mood', value: checkin.vitalityScore },
                ].map(function(d) {
                  return (
                    <div key={d.label}>
                      <div style={{
                        fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 11px)',
                        fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase',
                        color: P.TF, marginBottom: 6,
                      }}>{d.label}</div>
                      <div style={{
                        fontFamily: FF, fontSize: 'clamp(24px, 2.6vw, 32px)',
                        fontWeight: 400, color: P.INK,
                        lineHeight: 1, letterSpacing: '-0.012em',
                      }}>{d.value} <span style={{
                        fontSize: '0.5em', color: P.TF, fontWeight: 700,
                      }}>/ 10</span></div>
                    </div>
                  );
                })}
              </div>
              {checkin.freeText && (
                <div style={{
                  padding: 'clamp(14px, 1.6vw, 20px) clamp(16px, 1.8vw, 22px)',
                  background: 'rgba(232,221,196,0.55)',
                  border: '1px solid ' + P.SAND3,
                  borderLeft: '3px solid ' + P.GO,
                  marginBottom: 'clamp(18px, 2vw, 24px)',
                }}>
                  <div style={{
                    fontFamily: FF, fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase',
                    color: P.BR2, marginBottom: 8,
                  }}>You wrote</div>
                  <p style={{
                    fontFamily: FF, fontSize: 'clamp(15px, 1.3vw, 17px)',
                    fontStyle: 'italic', color: P.TX,
                    lineHeight: 1.7, margin: 0,
                  }}>{checkin.freeText}</p>
                </div>
              )}
              <div style={{ textAlign: 'right' }}>
                <BtnGhost onClick={function() { setResubmitOpen(true); setSubmitSuccess(false); }}>
                  Update my check-in
                </BtnGhost>
              </div>
            </div>
          ) : (
            // Form view
            <div style={{
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              padding: 'clamp(24px, 2.8vw, 32px)',
            }}>
              <p style={{
                fontFamily: FF, fontSize: 'clamp(16px, 1.4vw, 18px)',
                color: P.TL, lineHeight: 1.65, fontStyle: 'italic',
                margin: '0 0 clamp(24px, 2.8vw, 32px)',
              }}>
                How are you this week? Move each slider to where you feel you are right now.
              </p>

              <CheckinSliders
                values={scores}
                disabled={submitting}
                onChange={setScores}
              />

              <div style={{ marginTop: 'clamp(20px, 2.4vw, 28px)' }}>
                <label style={{
                  display: 'block',
                  fontFamily: FF, fontSize: 'clamp(11px, 0.95vw, 13px)',
                  fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase',
                  color: P.BR2, marginBottom: 10,
                }}>Anything else?</label>
                <p style={{
                  fontFamily: FF, fontSize: 'clamp(13px, 1.1vw, 14px)',
                  fontStyle: 'italic', color: P.TF,
                  margin: '0 0 10px', lineHeight: 1.55,
                }}>Optional. Anything you&rsquo;d like your care team to know this week.</p>
                <textarea
                  value={freeText}
                  onChange={function(e) { setFreeText(e.target.value); }}
                  disabled={submitting}
                  rows={4}
                  placeholder="Optional"
                  style={{
                    width: '100%',
                    fontFamily: FF,
                    fontSize: 'clamp(15px, 1.3vw, 17px)',
                    lineHeight: 1.6,
                    padding: '14px 16px',
                    border: '1px solid ' + P.SAND3,
                    background: P.PAP,
                    color: P.INK,
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {submitError && (
                <p style={{
                  fontFamily: FF, fontSize: 'clamp(15px, 1.3vw, 17px)',
                  fontStyle: 'italic', color: P.RED,
                  marginTop: 16, marginBottom: 0,
                }}>{submitError}</p>
              )}

              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
                marginTop: 'clamp(24px, 2.8vw, 32px)',
                flexWrap: 'wrap',
              }}>
                {resubmitOpen && (
                  <BtnGhost onClick={function() { setResubmitOpen(false); setSubmitError(null); }} disabled={submitting}>
                    Cancel
                  </BtnGhost>
                )}
                <button
                  onClick={handleSubmitCheckin}
                  disabled={submitting}
                  style={{
                    fontFamily: FS,
                    fontSize: 'clamp(12px, 1.1vw, 14px)',
                    fontWeight: 600,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    padding: 'clamp(14px, 1.6vw, 18px) clamp(28px, 3.6vw, 44px)',
                    background: submitting ? P.TL : (submitSuccess ? P.GREEN : P.INK),
                    color: P.PAP,
                    border: 'none',
                    cursor: submitting ? 'wait' : 'pointer',
                    transition: 'background 0.2s',
                  }}>
                  {submitting ? 'Submitting…' : submitSuccess ? 'Submitted' : 'Submit check-in'}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ─── Section 6: Reflection prompt (small, at end) ─── */}
        {mod.reflectionPrompt && (
          <section style={{ marginBottom: 'clamp(40px, 4.5vw, 56px)' }}>
            <SectionEyebrow text="Reflection" />
            <p style={{
              ...T.premise,
              fontSize: 'clamp(20px, 2.2vw, 28px)',
              color: P.BR2,
              lineHeight: 1.45,
              margin: 0,
            }}>{mod.reflectionPrompt}</p>
          </section>
        )}

        {/* Bottom return-to-program link */}
        <div style={{ textAlign: 'center', marginTop: 'clamp(48px, 6vw, 80px)' }}>
          <BtnGhost onClick={function() { if (onNavigate) onNavigate('program'); }}>
            Back to program
          </BtnGhost>
        </div>

      </main>
    </div>
  );
}
